import React from "react";
import { Link, type LinkProps } from "react-router-dom";

import { faCaretDown } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon, type FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import type { LocationState } from "history";

import styles from "./Button.module.css";

/** "wide" is deprecated. */
export const buttonSizes = ["normal", "slim", "wide"] as const;
export const buttonVariants = [
	"primary",
	"primary-ai",
	"secondary",
	"tertiary",
	"destructive",
	"billing",
	"placeholder",
	"outlined",
	"accent",
] as const;

export type FellowButtonProps = {
	size?: (typeof buttonSizes)[number];
	variant?: (typeof buttonVariants)[number];
	disabled?: boolean;
	/** @deprecated Add a className instead. */
	round?: boolean;
};

export type ButtonProps = Omit<React.HTMLProps<HTMLButtonElement>, "ref" | "size"> &
	React.ButtonHTMLAttributes<HTMLButtonElement> &
	FellowButtonProps;

/**
 * This is our button. There are many like it, but this one is ours.
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, size = "normal", variant = "secondary", round = false, type = "button", children, ...rest }, ref) => {
		const classNames = cx(styles.button, styles[`size-${size}`], styles[`variant-${variant}`], className, {
			[styles.disabled]: rest.disabled,
			[styles.round]: round,
		});

		return (
			<button ref={ref} {...rest} type={type} className={classNames}>
				{children}
			</button>
		);
	},
);

Button.displayName = "Button";

export type LinkButtonProps<LS extends LocationState> = LinkProps<LS> & FellowButtonProps;

/**
 * A button that can trigger navigation.
 */
export const LinkButton = <T extends LocationState>({
	className,
	size = "normal",
	variant = "secondary",
	round = false,
	children,
	...rest
}: LinkButtonProps<T>) => {
	const classNames = cx(styles.button, styles[`size-${size}`], styles[`variant-${variant}`], className, {
		[styles.disabled]: rest.disabled,
		[styles.round]: round,
	});
	return (
		<Link<T> {...rest} className={classNames}>
			{children}
		</Link>
	);
};

LinkButton.displayName = "LinkButton";

export type AnchorButtonProps = Omit<React.HTMLProps<HTMLAnchorElement>, "ref" | "size"> &
	React.AnchorHTMLAttributes<HTMLAnchorElement> &
	FellowButtonProps;

/**
 * An anchor that looks like a button.
 */
export const AnchorButton = ({
	className,
	size = "normal",
	variant = "secondary",
	round = false,
	children,
	...rest
}: AnchorButtonProps) => {
	const classNames = cx(styles.button, styles[`size-${size}`], styles[`variant-${variant}`], className, {
		[styles.disabled]: rest.disabled,
		[styles.round]: round,
	});
	return (
		<a {...rest} className={classNames}>
			{children}
		</a>
	);
};

AnchorButton.displayName = "AnchorButton";

export const LabelIconForButton = React.forwardRef<SVGSVGElement, FontAwesomeIconProps & { side: "left" | "right" }>(
	({ icon, className, side, ...rest }, ref) => {
		return (
			<FontAwesomeIcon
				ref={ref}
				icon={icon}
				className={cx(styles.labelIcon, styles[`labelIcon-${side}`], className)}
				{...rest}
			/>
		);
	},
);

LabelIconForButton.displayName = "LabelIconForButton";

export const CaretForMenuTriggerButton = React.forwardRef<SVGSVGElement, Omit<FontAwesomeIconProps, "icon">>(
	({ className, ...rest }, ref) => {
		return (
			<LabelIconForButton
				ref={ref}
				icon={faCaretDown}
				side="right"
				className={cx(styles.menuCaret, className)}
				{...rest}
			/>
		);
	},
);

CaretForMenuTriggerButton.displayName = "CaretForMenuTriggerButton";

export default Button;
